/**
 * generateMnemonic — picks a memorable `{adjective}-{noun}-{4digits}` for a
 * project's email alias (`{mnemonic}@m.eleplan.com`).
 *
 * The address is editable (ProjectInboxSetupScreen + ProjectSettingsScreen
 * both let the user type a custom one), but the auto-generated default is
 * what most users will keep — so the pools below are curated to be ALWAYS
 * PLEASANT: no negative or ambiguous adjectives, no nouns that read awkward,
 * nothing a caregiver would be embarrassed to read aloud to a doctor.
 *
 * ~50 × ~65 = 3,250 word-pairs × 9,000 four-digit suffixes ≈ 29M unique
 * mnemonics. Collision-safe at any plausible scale and short enough that
 * the address reads as a word rather than a string of noise.
 *
 * Backward compat: existing projects keep their stored mnemonic (whatever
 * format they were generated with). This function only governs new ones.
 */

// Curated: positive or neutral, single syllable preferred, never medical /
// religious / demographic. Reviewed pairwise — no combination produces
// negative connotation.
const ADJ = [
  'able', 'airy', 'agile', 'apt', 'balmy', 'beaming', 'blithe',
  'bold', 'brave', 'breezy', 'bright', 'brisk', 'calm', 'cheery',
  'classy', 'clean', 'clear', 'clever', 'cool', 'cozy', 'crisp',
  'dapper', 'deft', 'eager', 'earnest', 'easy', 'fair', 'fine',
  'fleet', 'fresh', 'gentle', 'glad', 'good', 'happy', 'jolly',
  'jovial', 'keen', 'kind', 'light', 'lively', 'lucky', 'merry',
  'mild', 'neat', 'nimble', 'noble', 'plush', 'pure', 'quick',
  'quiet', 'rapid', 'rosy', 'sharp', 'silky', 'smart', 'snug',
  'soft', 'spry', 'steady', 'still', 'sunny', 'sweet', 'swift',
  'tidy', 'true', 'vivid', 'warm', 'wise',
];

// Curated: nature, simple objects, animals — nothing that sounds clinical
// or could be mistaken for a real surname / brand. No predators that read
// aggressive, no plants with medical associations.
const NOUN = [
  'bay', 'birch', 'bloom', 'brook', 'breeze', 'cliff', 'cloud',
  'clover', 'cove', 'creek', 'crest', 'dale', 'dawn', 'deer',
  'dell', 'dove', 'dune', 'falls', 'fawn', 'fern', 'field',
  'finch', 'fjord', 'fog', 'forest', 'glade', 'glen', 'grove',
  'harbor', 'hare', 'haven', 'heron', 'hill', 'island', 'jay',
  'lake', 'leaf', 'linden', 'marsh', 'meadow', 'mist', 'moon',
  'moss', 'oak', 'orchard', 'otter', 'palm', 'peak', 'pebble',
  'petal', 'pine', 'pond', 'reef', 'reed', 'ridge', 'river',
  'robin', 'sage', 'sky', 'shore', 'spring', 'star', 'stone',
  'sun', 'swan', 'tide', 'trail', 'vale', 'valley', 'wave',
  'willow', 'wren',
];

const pick = <T>(xs: ReadonlyArray<T>): T => xs[Math.floor(Math.random() * xs.length)];

export function generateMnemonic(): string {
  const a = pick(ADJ);
  const n = pick(NOUN);
  const d = Math.floor(1000 + Math.random() * 9000).toString(); // 4 digits, never leading-zero
  return `${a}-${n}-${d}`;
}
